import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

import { FeatureNames, SubscriptionPlan } from '@vi-iot/client';

import { RemoteDiagnosticsBackgroundService, RoutingService } from '@services';
import {
  ActiveErrorsStore,
  AuthStore,
  BookingStore,
  DIAGNOSTICS_CONNECTION_STATUS,
  FeatureService,
  InformationPopupStore,
  InstallationDetailsStore,
  Platform,
  RemoteDiagnosticsStore,
  RemoteDiagnosticsSnapshotsService,
  RemoteConfigStore,
  DevicesStore,
  localStorageSessionsKey,
  RemoteDiagnosticsService,
  InstallationFeaturesStore,
} from '@store';

import { ActivatedRoute } from '@angular/router';
import { withLatestFrom } from 'rxjs/operators';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { SocketIoService } from '../../shared/socket-io/socket-io.service';

import { DiagnosticsOverlayTypeEnum } from '../installation/installation-overlay.model';
import { InformationPopupPage } from '../../shared/components/information-popup/information-popup.model';

@Component({
  selector: 'app-diagnostics-overlay',
  templateUrl: './diagnostics-overlay.component.html',
  styleUrls: ['./diagnostics-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('overlayContainer') overlayContainer: ElementRef;

  connectionStatus: DIAGNOSTICS_CONNECTION_STATUS;
  isDiagnosticsAvailable: boolean;
  devicePlatform: Platform;
  installationPackage: SubscriptionPlan;
  isDemo: boolean;

  readonly diagnosticsOverlayTypeEnum = DiagnosticsOverlayTypeEnum;
  readonly diagnosticsConnectionStatus = DIAGNOSTICS_CONNECTION_STATUS;
  readonly platform = Platform;
  readonly bookingPlans = SubscriptionPlan;
  readonly InformationPopupPage = InformationPopupPage;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  @Input() activeDiagnosticOverlay: DiagnosticsOverlayTypeEnum;
  @Input() installationId: number;
  @Input() gatewaySerial: string;
  @Input() boilerSerial: string;

  constructor(
    public activeErrorsStore: ActiveErrorsStore,
    public installationDetailsStore: InstallationDetailsStore,
    public bookingStore: BookingStore,
    public remoteDiagnosticsStore: RemoteDiagnosticsStore,
    public remoteConfigStore: RemoteConfigStore,
    public devicesStore: DevicesStore,
    private cdr: ChangeDetectorRef,
    private authStore: AuthStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private socket: SocketIoService,
    private featureService: FeatureService,
    private remoteDiagnosticsBackgroundService: RemoteDiagnosticsBackgroundService,
    private informationPopupStore: InformationPopupStore,
    private appInsights: AppInsightsMonitoringService,
    private remoteDiagnosticsSnapshotsService: RemoteDiagnosticsSnapshotsService,
    private activatedRoute: ActivatedRoute,
    private routingService: RoutingService,
    private remoteDiagnosticsService: RemoteDiagnosticsService
  ) {}

  ngOnInit(): void {
    this.informationPopupStore.loadPageSettings();

    this.installationDetailsStore
      .isDiagnosticsAvailable(this.gatewaySerial)
      .pipe(
        tap((isDiagnosticsAvailable) => {
          // debugger;
          if (isDiagnosticsAvailable) {
            this.featureService.fetchGatewayFeature(FeatureNames.gatewayRemoteDiagnostics);
            this.remoteDiagnosticsBackgroundService.initLogicalLinkListInterval(this.gatewaySerial);
          }
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((isDiagnosticsAvailable) => {
        this.isDiagnosticsAvailable = isDiagnosticsAvailable;
        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsStore
      .isActiveSessionAvailable(this.gatewaySerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isActiveSessionAvailable) => {
        if (isActiveSessionAvailable) {
          this.remoteDiagnosticsStore.setConnectionStatus(DIAGNOSTICS_CONNECTION_STATUS.ACTIVE);

          this.appInsights.logEvent(
            APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.CONNECTION_STATUS_CONNECTED,
            LogLevelEnum.ERROR
          );
        } else {
          this.remoteDiagnosticsStore.setConnectionStatus(DIAGNOSTICS_CONNECTION_STATUS.INACTIVE);

          this.appInsights.logEvent(
            APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.CONNECTION_STATUS_INACTIVE,
            LogLevelEnum.TRACE
          );
        }

        this.cdr.detectChanges();
      });

    this.remoteDiagnosticsStore
      .getConnectionStatus()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((status) => {
        this.connectionStatus = status;
        this.cdr.detectChanges();
        if (status === DIAGNOSTICS_CONNECTION_STATUS.FAILED) {
          this.remoteDiagnosticsBackgroundService.stopIntervals();
        }
      });

    this.installationDetailsStore
      .getMainDevicePlatform()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((platform) => {
        this.devicePlatform = platform;
        this.cdr.detectChanges();
      });

    this.installationDetailsStore
      .selectInstallationSubscriptionPlan(this.boilerSerial)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((installationPackage) => {
        this.installationPackage = installationPackage;
        this.cdr.markForCheck();
      });

    this.authStore.isDemo$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((demoStatus) => {
      this.isDemo = demoStatus;

      if (this.isDemo) {
        const localStorageSessionKey = `${localStorageSessionsKey}${this.gatewaySerial}`;
        const isLocalStorageKey = localStorage.getItem(localStorageSessionKey);

        if (isLocalStorageKey) {
          localStorage.removeItem(localStorageSessionKey);
        }
      }
    });

    this.activatedRoute.queryParams
      .pipe(
        take(1),
        withLatestFrom(
          this.installationDetailsStore.getDeviceSerialForRemoteDiagnostics$,
          this.remoteDiagnosticsStore.getConnectedDeviceSerial(this.gatewaySerial)
        )
      )
      .subscribe(([params, deviceSerial, connectedDeviceSerial]) => {
        if (connectedDeviceSerial && params?.deviceSerial !== connectedDeviceSerial) {
          this.routingService.updateUrl({ deviceSerial: connectedDeviceSerial });

          return;
        }

        if (params?.deviceSerial !== deviceSerial) {
          this.routingService.updateUrl({ deviceSerial });
        }
        this.cdr.markForCheck();
      });

    this.remoteDiagnosticsService.scrollToTopValue$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((value) => {
        // Due to complex DOM structure (overlay) neither window nor ViewportScroller works
        this.overlayContainer.nativeElement.scrollTo({ top: value, behavior: 'smooth' });
      });
  }

  private disableDemoModeConnection(): void {
    this.installationFeaturesStore.getMockedFeature(
      'remoteDiagnosticsReady',
      FeatureNames.gatewayRemoteDiagnostics
    );
    this.remoteDiagnosticsStore.setConnectionStatus(DIAGNOSTICS_CONNECTION_STATUS.INACTIVE);
  }

  ngOnDestroy(): void {
    this.socket.disconnect();
    this.socket.reconnect();
    this.remoteDiagnosticsBackgroundService.stopIntervals();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    if (this.isDemo && this.connectionStatus !== DIAGNOSTICS_CONNECTION_STATUS.INACTIVE) {
      this.disableDemoModeConnection();
      this.remoteDiagnosticsSnapshotsService.backupsForDemo = [];
      this.remoteDiagnosticsSnapshotsService.snapshotsForDemo = [];
      this.remoteDiagnosticsSnapshotsService.snapshotDetailsForDemo = [];
    }
  }
}
